import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import "./questionScreen.css";
import logo from "../../img/assets/logo.png";
import lifelineUsed from "../../img/assets/lifelines-used.png";
import lifelineAvailable from "../../img/assets/lifelines-available.png";
import buzzSound from "../../audio/buzz-sound.mp3";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { gameGetState } from "../../admin/functions/gameApi";

function splitRule(line) {
  const text = String(line || "").trim();
  const sepIndex = text.indexOf(":");
  if (sepIndex <= 0 || sepIndex === text.length - 1) {
    return { title: null, body: text };
  }

  return {
    title: text.slice(0, sepIndex).trim(),
    body: text.slice(sepIndex + 1).trim(),
  };
}

function secondsLeft(deadlineMs) {
  const d = Number(deadlineMs);
  if (!Number.isFinite(d)) return null;
  return Math.max(0, Math.ceil((d - Date.now()) / 1000));
}

function sumTeamPoints(leaderboard, teamLetter) {
  let sum = 0;
  leaderboard.forEach((row) => {
    if (String(row.team) === teamLetter) {
      sum += Number(row.points) || 0;
    }
  });
  return sum;
}

function TeamPanel({ title, players, leaderboard, teamLetter, selectedPlayerId }) {
  const teamTotal = sumTeamPoints(leaderboard, teamLetter);

  return (
    <aside className="question-team-panel">
      <h3 className="question-team-panel__title">{title}</h3>
      <div className="question-team-panel__list">
        {players.length === 0 ? (
          <p className="question-team-panel__rank question-team-panel__rank--empty">—</p>
        ) : (
          players.map((p, index) => {
            const isSel = selectedPlayerId != null && Number(p.id) === Number(selectedPlayerId);
            return (
              <p
                key={p.id}
                className={`question-team-panel__rank ${isSel ? "question-team-panel__rank--selected question-team-panel__rank--highlight" : ""}`}
              >
                {index + 1}# {isSel ? <strong>{p.player_name}</strong> : p.player_name}
              </p>
            );
          })
        )}
      </div>
      {players.length > 0 ? (
        <>
          <div className="question-team-panel__divider" />
          <p className="question-team-panel__points">Points - {teamTotal}</p>
        </>
      ) : null}
    </aside>
  );
}

function LifelineStrip({ label, usedCount }) {
  const n = Math.min(3, Math.max(0, Number(usedCount) || 0));
  return (
    <div className="question-lifelines__group" aria-label={`${label} lifelines`}>
      <span className="question-lifelines__label">{label}</span>
      <div className="question-lifelines__icons">
        {[0, 1, 2].map((i) => (
          <img
            key={`${label}-${i}`}
            src={i < n ? lifelineUsed : lifelineAvailable}
            alt={i < n ? "Lifeline used" : "Lifeline available"}
          />
        ))}
      </div>
    </div>
  );
}

export default function QuestionScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector(selectAdminToken);
  const themeFromStore = useSelector((state) => state.theme?.currentTheme);
  const themeData = useSelector((state) => state.theme?.data);
  const currentTheme = themeFromStore || localStorage.getItem("currentTheme");
  const prevLeaderboardVisibleRef = useRef(null);
  const lastQuestionIdRef = useRef(null);
  const lastProcessedSubmitLogIdRef = useRef(null);
  const lastProcessedRevealLogIdRef = useRef(null);
  const hadAnyReleaseRef = useRef(false);
  const confettiTimerRef = useRef(null);
  const pointPopupTimerRef = useRef(null);
  const flipTimerRef = useRef(null);
  const strikePopupTimerRef = useRef(null);
  const buzzAudioRef = useRef(null);
  const prevStrikeARef = useRef(null);
  const prevStrikeBRef = useRef(null);

  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [state, setState] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [visibleOptionNos, setVisibleOptionNos] = useState([]);
  const [latestSubmitLogId, setLatestSubmitLogId] = useState(null);
  const [latestSubmitOptionNo, setLatestSubmitOptionNo] = useState(null);
  const [latestRevealLogId, setLatestRevealLogId] = useState(null);
  const [latestRevealOptionNo, setLatestRevealOptionNo] = useState(null);
  const [latestSubmitNo, setLatestSubmitNo] = useState(null);
  const [latestRevealNo, setLatestRevealNo] = useState(null);
  const [questionTimerTotalSeconds, setQuestionTimerTotalSeconds] = useState(30);
  const [discussionTimerTotalSeconds, setDiscussionTimerTotalSeconds] = useState(30);
  const [animatedCorrectOptionNo, setAnimatedCorrectOptionNo] = useState(null);
  const [pointPopupValue, setPointPopupValue] = useState(null);
  const [showStrikeCross, setShowStrikeCross] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 100);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!token || !currentTheme) return undefined;
    let cancelled = false;

    async function poll() {
      try {
        const data = await gameGetState({ token, currentTheme });
        if (cancelled || !data.success) return;
        setState(data.state || null);
        setPlayers(Array.isArray(data.players) ? data.players : []);
        setLeaderboard(Array.isArray(data.leaderboard) ? data.leaderboard : []);
        setCurrentQuestion(data.currentQuestion || null);
        setVisibleOptionNos(
          Array.isArray(data.visibleOptionNos)
            ? data.visibleOptionNos
                .map((n) => Number(n))
                .filter((n) => Number.isFinite(n) && n >= 1 && n <= 8)
            : []
        );
        setLatestSubmitLogId(Number(data.latestSubmitLogId) || null);
        setLatestSubmitOptionNo(Number(data.latestSubmitOptionNo) || null);
        setLatestRevealLogId(Number(data.latestRevealLogId) || null);
        setLatestRevealOptionNo(Number(data.latestRevealOptionNo) || null);
        const qTotal = Number(data.questionTimerTotalSeconds);
        setQuestionTimerTotalSeconds(Number.isFinite(qTotal) && qTotal > 0 ? qTotal : 30);
        const dTotal = Number(data.discussionTimerTotalSeconds);
        setDiscussionTimerTotalSeconds(Number.isFinite(dTotal) && dTotal > 0 ? dTotal : 30);
      } catch {
        /* ignore transient errors */
      }
    }

    poll();
    const interval = window.setInterval(poll, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [token, currentTheme]);

  useEffect(() => {
    if (!currentTheme) return;
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!token || !currentTheme || state == null) return;
    const lb = Number(state.leaderboard_visible);
    if (!Number.isFinite(lb)) return;
    const prev = prevLeaderboardVisibleRef.current;
    if (lb === 1 && prev !== 1) {
      navigate("/leaderboard");
    }
    prevLeaderboardVisibleRef.current = lb;
  }, [token, currentTheme, state, navigate]);

  const teamA = useMemo(
    () => [...players].filter((p) => p.team === "A").sort((a, b) => Number(a.id) - Number(b.id)),
    [players]
  );
  const teamB = useMemo(
    () => [...players].filter((p) => p.team === "B").sort((a, b) => Number(a.id) - Number(b.id)),
    [players]
  );

  const isGameMode = (state?.mode || "game") === "game";
  const teamsReady = Number(state?.teams_created) === 1;
  const questionReleased = Number(state?.question_released) === 1;
  useEffect(() => {
    if (questionReleased) hadAnyReleaseRef.current = true;
  }, [questionReleased]);

  useEffect(() => {
    const strikeA = Number(state?.strike_a);
    const strikeB = Number(state?.strike_b);
    if (!Number.isFinite(strikeA) || !Number.isFinite(strikeB)) return;
    if (prevStrikeARef.current == null || prevStrikeBRef.current == null) {
      prevStrikeARef.current = strikeA;
      prevStrikeBRef.current = strikeB;
      return;
    }
    const incremented = strikeA > prevStrikeARef.current || strikeB > prevStrikeBRef.current;
    prevStrikeARef.current = strikeA;
    prevStrikeBRef.current = strikeB;
    if (!incremented) return;

    if (!buzzAudioRef.current) {
      buzzAudioRef.current = new Audio(buzzSound);
      buzzAudioRef.current.preload = "auto";
    }
    const audio = buzzAudioRef.current;
    try {
      audio.pause();
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => undefined);
      }
    } catch {
      // Ignore autoplay/device restrictions.
    }

    setShowStrikeCross(false);
    window.setTimeout(() => setShowStrikeCross(true), 0);
    if (strikePopupTimerRef.current) {
      window.clearTimeout(strikePopupTimerRef.current);
    }
    strikePopupTimerRef.current = window.setTimeout(() => setShowStrikeCross(false), 3000);
  }, [state?.strike_a, state?.strike_b]);

  const selectedPlayerId = state?.selected_player_id;
  const hostSelectedOptionNo = state?.selected_option_no != null ? Number(state.selected_option_no) : null;
  const currentQuestionId = state?.current_question_id != null ? Number(state.current_question_id) : null;

  const discussionDeadline = Number(state?.discussion_deadline_ms);
  const discussionActive =
    isGameMode && Number.isFinite(discussionDeadline) && discussionDeadline > Date.now();
  const discussionExpired =
    isGameMode && Number.isFinite(discussionDeadline) && discussionDeadline <= Date.now();

  const questionDeadline = Number(state?.question_deadline_ms);
  const questionExpired =
    isGameMode && questionReleased && Number.isFinite(questionDeadline) && questionDeadline <= Date.now();
  const questionTimerLeft =
    isGameMode && questionReleased && !discussionActive && Number.isFinite(questionDeadline)
      ? secondsLeft(questionDeadline)
      : null;

  const discussionTimerLeft =
    isGameMode && discussionActive ? secondsLeft(discussionDeadline) : null;

  const timerDisplay = discussionTimerLeft != null ? discussionTimerLeft : questionTimerLeft;
  const showTimerChip = timerDisplay != null;
  const nowMs = Date.now();
  const activeTimerTotalMs =
    discussionTimerLeft != null
      ? Math.max(1, Number(discussionTimerTotalSeconds) * 1000)
      : Math.max(1, Number(questionTimerTotalSeconds) * 1000);
  const activeDeadlineMs = discussionTimerLeft != null ? discussionDeadline : questionDeadline;
  const activeElapsedMs =
    Number.isFinite(activeDeadlineMs) ? Math.max(0, activeTimerTotalMs - (activeDeadlineMs - nowMs)) : 0;
  const boardProgress = isGameMode
    ? Math.min(1, Math.max(0, activeElapsedMs / activeTimerTotalMs))
    : 0;
  const showDiscussionWaiting = !questionReleased && discussionExpired;
  const showQuestionWaiting = questionReleased && questionExpired;
  const showWaitingForHost = showDiscussionWaiting || showQuestionWaiting;
  const showIntroText =
    !questionReleased &&
    !currentQuestion?.question &&
    !hadAnyReleaseRef.current;
  const centerText = discussionActive
    ? "Discussion time"
    : currentQuestion?.question
    ? currentQuestion.question
    : showIntroText
    ? "We will begin shortly"
    : showWaitingForHost
    ? "Waiting for host..."
    : "";
  const optionsByNo = currentQuestion?.optionsWithPoints || [];
  const visibleOptions = visibleOptionNos
    .map((no) => {
      const idx = no - 1;
      if (idx < 0 || idx >= optionsByNo.length) return null;
      return { optionNo: no, option: optionsByNo[idx] };
    })
    .filter(Boolean);
  const showOptions = visibleOptions.length > 0 && questionReleased && !discussionActive;

  useEffect(() => {
    if (lastQuestionIdRef.current === currentQuestionId) return;
    lastQuestionIdRef.current = currentQuestionId;
    lastProcessedSubmitLogIdRef.current = latestSubmitLogId;
    lastProcessedRevealLogIdRef.current = latestRevealLogId;
    setLatestSubmitNo(null);
    setLatestRevealNo(null);
    setAnimatedCorrectOptionNo(null);
    setPointPopupValue(null);
    if (confettiTimerRef.current) {
      window.clearTimeout(confettiTimerRef.current);
      confettiTimerRef.current = null;
    }
    if (pointPopupTimerRef.current) {
      window.clearTimeout(pointPopupTimerRef.current);
      pointPopupTimerRef.current = null;
    }
    if (flipTimerRef.current) {
      window.clearTimeout(flipTimerRef.current);
      flipTimerRef.current = null;
    }
  }, [currentQuestionId, latestSubmitLogId, latestRevealLogId]);

  useEffect(() => {
    const newSubmitDetected =
      currentQuestionId != null &&
      latestSubmitLogId != null &&
      latestSubmitOptionNo != null &&
      latestSubmitLogId !== lastProcessedSubmitLogIdRef.current;
    if (newSubmitDetected) {
      const selectedOption = Number(latestSubmitOptionNo);
      const selectedOptionPoint =
        currentQuestion?.optionsWithPoints?.[selectedOption - 1]?.point;
      const popupPoint = Number(selectedOptionPoint);
      setLatestSubmitNo(selectedOption);
      setLatestRevealNo(null);
      setAnimatedCorrectOptionNo(selectedOption);
      setPointPopupValue(Number.isFinite(popupPoint) ? popupPoint : 0);
      const burst = (origin, angle, opts = {}) =>
        confetti({
          particleCount: 72,
          angle,
          spread: 56,
          startVelocity: 42,
          gravity: 0.62,
          origin,
          zIndex: 9999,
          scalar: 1.05,
          ...opts,
        });
      burst({ x: 0.06, y: 0.88 }, 62);
      if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
      if (pointPopupTimerRef.current) window.clearTimeout(pointPopupTimerRef.current);
      confettiTimerRef.current = window.setTimeout(() => {
        burst({ x: 0.94, y: 0.88 }, 118);
        window.setTimeout(() => {
          burst({ x: 0.5, y: 0.35 }, 90, {
            particleCount: 38,
            spread: 70,
            scalar: 0.85,
            startVelocity: 28,
          });
        }, 260);
      }, 85);
      pointPopupTimerRef.current = window.setTimeout(() => setPointPopupValue(null), 1300);
      if (flipTimerRef.current) window.clearTimeout(flipTimerRef.current);
      flipTimerRef.current = window.setTimeout(() => setAnimatedCorrectOptionNo(null), 650);
    }
    lastProcessedSubmitLogIdRef.current = latestSubmitLogId;
  }, [latestSubmitLogId, latestSubmitOptionNo, currentQuestionId, currentQuestion]);

  useEffect(() => {
    const newRevealDetected =
      currentQuestionId != null &&
      latestRevealLogId != null &&
      latestRevealOptionNo != null &&
      latestRevealLogId !== lastProcessedRevealLogIdRef.current;
    if (newRevealDetected) {
      const revealNo = Number(latestRevealOptionNo);
      if (!isGameMode) {
        setLatestSubmitNo(revealNo);
        setLatestRevealNo(null);
        setAnimatedCorrectOptionNo(revealNo);
        const burst = (origin, angle, opts = {}) =>
          confetti({
            particleCount: 72,
            angle,
            spread: 56,
            startVelocity: 42,
            gravity: 0.62,
            origin,
            zIndex: 9999,
            scalar: 1.05,
            ...opts,
          });
        burst({ x: 0.06, y: 0.88 }, 62);
        if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
        confettiTimerRef.current = window.setTimeout(() => {
          burst({ x: 0.94, y: 0.88 }, 118);
          window.setTimeout(() => {
            burst({ x: 0.5, y: 0.35 }, 90, {
              particleCount: 38,
              spread: 70,
              scalar: 0.85,
              startVelocity: 28,
            });
          }, 260);
        }, 85);
        if (flipTimerRef.current) window.clearTimeout(flipTimerRef.current);
        flipTimerRef.current = window.setTimeout(() => setAnimatedCorrectOptionNo(null), 650);
      } else {
        setLatestRevealNo(revealNo);
        setLatestSubmitNo(null);
        setAnimatedCorrectOptionNo(null);
      }
      setPointPopupValue(null);
      if (pointPopupTimerRef.current) {
        window.clearTimeout(pointPopupTimerRef.current);
        pointPopupTimerRef.current = null;
      }
      if (flipTimerRef.current && isGameMode) {
        window.clearTimeout(flipTimerRef.current);
        flipTimerRef.current = null;
      }
    }
    lastProcessedRevealLogIdRef.current = latestRevealLogId;
  }, [latestRevealLogId, latestRevealOptionNo, currentQuestionId, isGameMode]);

  useEffect(() => {
    return () => {
      if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
      if (pointPopupTimerRef.current) window.clearTimeout(pointPopupTimerRef.current);
      if (flipTimerRef.current) window.clearTimeout(flipTimerRef.current);
      if (strikePopupTimerRef.current) window.clearTimeout(strikePopupTimerRef.current);
    };
  }, []);

  const activeRulesKey = isGameMode ? "rules_game" : "rules_crowd";
  let rulesListForHowTo = themeData?.[activeRulesKey];
  if (!Array.isArray(rulesListForHowTo)) {
    rulesListForHowTo = themeData?.rules;
  }
  const howToPlayRules = Array.isArray(rulesListForHowTo)
    ? rulesListForHowTo
        .map((rule) => String(rule || "").trim())
        .filter(Boolean)
    : [];

  if (!token || !currentTheme) {
    return (
      <div className="question-screen question-screen--waiting">
        <p className="question-screen__waiting-msg">Sign in and select a theme to view the live game.</p>
      </div>
    );
  }

  return (
    <div className="question-screen">
      <div className="question-screen__actions">
        <button
          type="button"
          className="question-howto-trigger"
          onClick={() => setShowHowToPlay(true)}
        >
          How to Play
        </button>
      </div>

      <div className={`question-screen__top ${!isGameMode ? "question-screen__top--crowd" : ""}`}>
        {isGameMode && teamsReady ? (
          <TeamPanel
            title="TEAM A"
            players={teamA}
            leaderboard={leaderboard}
            teamLetter="A"
            selectedPlayerId={selectedPlayerId}
          />
        ) : isGameMode ? (
          <aside className="question-team-panel question-team-panel--muted" aria-hidden>
            <h3 className="question-team-panel__title">TEAM A</h3>
            <p className="question-team-panel__rank">Teams not set</p>
          </aside>
        ) : null}

        <main
          className={`question-board-wrap ${showTimerChip ? "" : "question-board-wrap--no-timer"}`}
        >
          {showTimerChip ? (
            <div className="question-timer-chip question-timer-chip--running" aria-live="polite">
              {timerDisplay}
            </div>
          ) : null}

          <section
            className={`question-board ${showTimerChip ? "question-board--timer-active" : ""}`}
            style={{ "--board-progress": boardProgress }}
          >
            <div
              className={`question-board__inner ${showOptions ? "question-board__inner--stack" : ""}`}
            >
              {centerText ? (
                <p
                  className={`question-board__text ${showOptions ? "question-board__text--with-options" : ""} ${
                    discussionActive ? "question-board__text--discussion" : "question-board__text--prompt"
                  }`}
                >
                  {centerText}
                </p>
              ) : (
                <p className="question-board__text question-board__text--placeholder">Waiting for host…</p>
              )}

              {showOptions ? (
                <div className="question-options" aria-label="Question options">
                  {visibleOptions.map(({ optionNo, option }) => {
                    const label = option?.text || "";
                    const pt = Number(option?.point) || 10;
                    const adminPick =
                      hostSelectedOptionNo != null &&
                      Number.isFinite(hostSelectedOptionNo) &&
                      hostSelectedOptionNo === optionNo;
                    const submitted = latestSubmitNo === optionNo;
                    const revealedBlue = latestRevealNo === optionNo;
                    const isCorrectAnimated = animatedCorrectOptionNo === optionNo;
                    return (
                      <div
                        className={`question-option-tile ${adminPick ? "question-option-tile--admin-pick" : ""} ${
                          submitted ? "question-option-tile--correct-green" : ""
                        } ${
                          revealedBlue ? "question-option-tile--reveal-blue" : ""
                        } ${
                          isCorrectAnimated ? "question-option-tile--flip-correct" : ""
                        } ${
                          !isGameMode ? "question-option-tile--crowd" : ""
                        }`}
                        key={`opt-${optionNo}`}
                        title={label}
                      >
                        <span className="question-option-tile__text">{label}</span>
                        {isGameMode ? <span className="question-option-tile__points">{pt}</span> : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="question-board__logo">
              <img src={logo} alt="Office Fun Feud logo" />
            </div>
            {pointPopupValue != null ? (
              <div className="question-point-popup" aria-live="polite">
                +{pointPopupValue}
              </div>
            ) : null}
            {showStrikeCross ? (
              <div className="question-strike-cross" aria-hidden>
                X
              </div>
            ) : null}
          </section>
        </main>

        {isGameMode && teamsReady ? (
          <TeamPanel
            title="TEAM B"
            players={teamB}
            leaderboard={leaderboard}
            teamLetter="B"
            selectedPlayerId={selectedPlayerId}
          />
        ) : isGameMode ? (
          <aside className="question-team-panel question-team-panel--muted" aria-hidden>
            <h3 className="question-team-panel__title">TEAM B</h3>
            <p className="question-team-panel__rank">Teams not set</p>
          </aside>
        ) : null}
      </div>

      {isGameMode && teamsReady ? (
        <div className="question-lifelines">
          <LifelineStrip label="Team A" usedCount={state?.strike_a} />
          <LifelineStrip label="Team B" usedCount={state?.strike_b} />
        </div>
      ) : null}

      {showHowToPlay ? (
        <div
          className="question-howto-overlay"
          onClick={() => setShowHowToPlay(false)}
          role="presentation"
        >
          <section
            className="question-howto-modal"
            role="dialog"
            aria-modal="true"
            aria-label="How to Play"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="question-howto-modal__header">
              <h2 className="question-howto-modal__title">How to Play</h2>
              <button
                type="button"
                className="question-howto-modal__close"
                onClick={() => setShowHowToPlay(false)}
                aria-label="Close How to Play"
              >
                Close
              </button>
            </div>

            {howToPlayRules.length === 0 ? (
              <p className="question-howto-list__text">No rules available.</p>
            ) : (
              <ul className="question-howto-list">
                {howToPlayRules.map((rule, index) => {
                  const { title, body } = splitRule(rule);
                  return (
                    <li className="question-howto-list__item" key={`${rule}-${index}`}>
                      <span className="question-howto-list__star" aria-hidden="true">
                        *
                      </span>
                      <p className="question-howto-list__text">
                        {title ? (
                          <>
                            <strong className="question-howto-rule-title">{title}:</strong>{" "}
                            <span className="question-howto-rule-body">{body}</span>
                          </>
                        ) : (
                          <span className="question-howto-rule-body">{body}</span>
                        )}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
